import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  CommonQuestionKeys,
  Field,
  FieldType,
  PaymentSchedule,
  TextInputType,
} from '@bybeam/platform-types';
import { YesNoOptions } from '@platform-ui/application/utils/index.js';
import { eq } from '../../ast/utils.js';
import STANDARD_COMPONENTS from '../standards/index.js';
import STANDARD_QUESTIONS from '../standards/questions.js';
import agreements from './agreements.js';

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'welcome',
      title: 'What is Water4All?',
      body: `**Water4All** is a water billing discount program to create more equitable access to water assistance for Baltimore City residents. 

**This program assists residents before water services are cut off or liens imposed.**

To learn more, go to [Water4All F.A.Q. (Frequently Asked Questions)](https://cityservices.baltimorecity.gov/Water4All)`,
    },
    STANDARD_COMPONENTS.definitions.introPages.ApplicationProcess,
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: 'Apply for Water4All',
        message:
          'You could be found eligible to receive water billing assistance. These funds are intended to help Baltimore City residents with income below 200% of the federal poverty level.',
      },
    ],
  },
  sections: [
    {
      key: 'tenantInformation',
      name: 'Water4All Tenant Information',
      overview: {
        title: 'Let’s start by verifying your tenant information',
        description:
          'In this section, applicants confirm their home address, date of birth, and Water4All eligibility.',
      },
      questionGroups: [
        {
          key: 'tenantInformation',
          name: 'Tenant Information',
          questions: [
            {
              key: 'waterBill',
              copy: {
                title: 'Do you have a water bill account number in your name?',
                intro: `
You can search your account number using this [link](https://pay.baltimorecity.gov/water).`,
              },
              fields: [
                {
                  key: 'waterBill.haveAccountNumber',
                  type: FieldType.RadioList,
                  options: YesNoOptions,
                },
                {
                  key: 'waterBill.accountNumber',
                  copy: 'Account Number',
                  type: FieldType.Text,
                  inputType: TextInputType.Text,
                  dynamicLogic: eq('waterBill.haveAccountNumber', 'yes'),
                  // TODO: I don't think this validation is correct, but since we aren't migrating
                  // Baltimore any time soon, not going to bother figuring out what it should be
                  // validation: {
                  //   required: true,
                  //   additionalValidation: ({ 'waterBill.accountNumber': value }) =>
                  //     isAccountNumberValid(value),
                  // },
                },
              ],
            },
            {
              key: 'documents.lease',
              copy: {
                title: 'Lease Upload',
                intro: 'Please upload a written lease that shows a separate charge for water.',
              },
              fields: [
                {
                  type: FieldType.Document,
                  key: 'documents.lease',
                },
              ],
              dynamicLogic: eq('waterBill.haveAccountNumber', 'no'),
            },
            {
              key: CommonQuestionKeys.MAILING_ADDRESS,
              copy: {
                title: 'What is your mailing address?',
                intro:
                  'Please complete the relevant fields of your home address. You must enter an address in Baltimore City to qualify.',
              },
              fields: [
                {
                  type: FieldType.Address,
                  key: CommonQuestionKeys.MAILING_ADDRESS,
                  filter: { state: 'MD' },
                },
              ],
            },
            {
              key: 'primaryResidence',
              copy: {
                title: 'Is this your primary residence?',
                intro:
                  'The Baltimore City Water4All program is only available for your primary residence.',
              },
              fields: [
                {
                  type: FieldType.RadioList,
                  key: 'primaryResidence',
                  options: YesNoOptions,
                },
              ],
            },
            STANDARD_COMPONENTS.definitions.questions.dateOfBirth,
          ],
        },
      ],
    },
    {
      key: 'householdAndEmployment',
      name: 'Household, Income & Employment Information',
      overview: {
        title: 'Let’s confirm your household information',
        description:
          'In this section, applicants record household members, detail the household’s annual income, as well as upload income and identity documentation.',
      },
      questionGroups: [
        {
          key: CommonQuestionKeys.HOUSEHOLD_MEMBERS,
          name: 'Household, Income & Employement Information',
          questions: [
            STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.HOUSEHOLD_MEMBERS],
          ],
        },
      ],
    },
    {
      key: 'additionalInformation',
      name: 'Additional Assistance Questionnaire',
      overview: {
        title: 'Additional Assistance Questionnaire',
        description:
          'In this section, applicants will complete 4 questions based upon additional assistance they receive.',
      },
      questionGroups: [
        {
          key: 'additionalInformation',
          name: 'Additional Assistance Questionnaire',
          questions: [
            {
              key: 'energyAssistance',
              copy: {
                title: 'Do you receive any energy assistance or subsidy?',
              },
              fields: [
                {
                  key: 'receivingEnergyAssistance',
                  type: FieldType.RadioList,
                  options: YesNoOptions,
                },
                {
                  key: 'energyAssistance',
                  copy: 'Please list the type of assistance.',
                  type: FieldType.Text,
                  inputType: TextInputType.Text,
                  dynamicLogic: eq('receivingEnergyAssistance', 'yes'),
                },
              ],
            },
            {
              key: 'publicAssistance',
              copy: {
                title: 'Do you receive any public assistance?',
                intro: '(Medical, Supplemental Social Security, Food Stamps)',
              },
              fields: [
                {
                  key: 'receivingPublicAssistance',
                  type: FieldType.RadioList,
                  options: YesNoOptions,
                },
                {
                  key: 'publicAssistance',
                  copy: 'Please list the type of assistance.',
                  type: FieldType.Text,
                  inputType: TextInputType.Text,
                  dynamicLogic: eq('receivingPublicAssistance', 'yes'),
                },
              ],
            },
            {
              key: 'disabilityBenefits',
              copy: {
                title: 'Do you receive veterans or social security disability benefits?',
              },
              fields: [
                {
                  key: 'receivingDisabilityBenefits',
                  type: FieldType.RadioList,
                  options: YesNoOptions,
                },
                {
                  key: 'disabilityBenefits',
                  copy: 'Please list the type of assistance.',
                  type: FieldType.Text,
                  inputType: TextInputType.Text,
                  dynamicLogic: eq('receivingDisabilityBenefits', 'yes'),
                },
              ],
            },
            {
              key: 'otherBenefits',
              copy: {
                title: 'Do you receive any other form of financial assistance?',
                intro: '(Relative, Child Support, Other)',
              },
              fields: [
                {
                  key: 'receivingOtherBenefits',
                  type: FieldType.RadioList,
                  options: YesNoOptions,
                },
                {
                  key: 'otherBenefits',
                  copy: 'Please list the type of assistance.',
                  type: FieldType.Text,
                  inputType: TextInputType.Text,
                  dynamicLogic: eq('receivingOtherBenefits', 'yes'),
                },
              ],
            },
            {
              key: 'paymentSchedule',
              copy: {
                title:
                  'Upon application approval, how would you like to receive your Water4All funding?',
              },
              fields: [
                {
                  type: FieldType.RadioList,
                  key: 'paymentSchedule',
                  options: [
                    {
                      label: 'One Time Payment',
                      value: PaymentSchedule.OneTime,
                    },
                    {
                      label: '12 Monthly Payments',
                      value: PaymentSchedule.Monthly,
                    },
                  ],
                },
              ],
            },
            mergeObjects(
              [
                STANDARD_COMPONENTS.definitions.questions.preferredPaymentMethod,
                {
                  copy: {
                    title:
                      'Upon application approval, how would you like to receive your Water4All funding?',
                  },
                } as ApplicationQuestion,
              ],
              'merge',
            ),
          ],
        },
      ],
    },
    mergeObjects(
      [
        STANDARD_COMPONENTS.definitions.sections.review,
        {
          questionGroups: [
            {
              key: CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER,
              name: 'Application Assistance',
              questions: [STANDARD_QUESTIONS[CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER]],
            },
            {
              key: CommonQuestionKeys.SIGNATURE,
              name: 'Attestation',
              questions: [
                {
                  key: CommonQuestionKeys.SIGNATURE,
                  copy: {
                    title: 'Water4All Attestation',
                    intro:
                      'Please type your signature below confirming that you have read and understand each statement:',
                  },
                  layout: 'panel',
                  fields: [
                    ...agreements.map(
                      (copy: string, idx): Field => ({
                        type: FieldType.Typography,
                        key: `submission.signature.agreements.${idx}`,
                        copy,
                        props: { variant: 'body', alignment: 'left' },
                      }),
                    ),
                    {
                      type: FieldType.Checkbox,
                      key: 'submission.signature.attestation',
                      copy: 'I have read and understand the above attestation and I attest that the information I provided in this application is accurate to the best of my knowledge.',
                    },
                    {
                      type: FieldType.Text,
                      key: 'submission.signature.signature',
                      inputType: TextInputType.Signature,
                      copy: 'Electronic Signature',
                    },
                  ],
                },
              ],
            },
          ],
        } as ApplicationSection,
      ],
      'overwrite',
    ),
  ],
};
export default CONFIG;
